import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import AppHOC from "../../../../generics/AppHOC";
import { useI18n } from "../../../../i18n/useI18n";
import { routeURLs } from "../../../../utils/enums";

// Utils
import { getCurrentSelectedSiteName } from "../../../../utils/common";

// Wrap Harbor custom elements for React usage
const [HbrIcon, HbrBreadcrumb, HbrBreadcrumbItem, HbrSpinner] = reactWrapper([
  "hbr-icon",
  "hbr-breadcrumb",
  "hbr-breadcrumb-item",
  "hbr-spinner"
]);

/**
 * FilterView
 * Renders the filter bar with breadcrumb navigation for the AppQoE details page.
 * Handles navigation back to the main optimization page.
 */
const FilterView = ({ globalFilter }) => {
  const { tViews } = useI18n('views');
  const navigate = useNavigate();
  // Handler for breadcrumb click to navigate back to optimization page
  const onBreadcrumbClick = () => {
    navigate(routeURLs.optimization);
  };


  // Memoize the site name to avoid unnecessary recalculations
  const siteName = useMemo(() => {
    return getCurrentSelectedSiteName(globalFilter);
  }, [globalFilter.sites]);  

  return (
    <nav
      className="app-qoe-details-filter-view"
      data-cy="app-qoe-details_filter"
      aria-label={tViews('a11yLabels.filterViewNavAriaLabel')}
    >
      <div className="flex-column header-filters-container">
        <HbrBreadcrumb aria-label={tViews('a11yLabels.breadcrumbAriaLabel')}>
          <HbrBreadcrumbItem onClick={onBreadcrumbClick}>
            <div className="flex-items">
              <HbrIcon
                name="arrow-left"
                label={tViews('a11yLabels.backNavigationAriaLabel')}
              />
              {tViews('optimization.appQoe')}
            </div>
          </HbrBreadcrumbItem>
        </HbrBreadcrumb>
        <div className="flex-items header-filters-bar">
          <div
            className="hbr-type-h1 app-qoe-title"
            role="heading"
            aria-level="1"
            aria-label={tViews('a11yLabels.appQoeDetailsHeadingAriaLabel')}
          >
            {tViews('optimization.appQoeDetails')}
          </div>
        </div>
        <div className="flex-items header-filters-bar">
          <div
            className="hbr-type-h3 app-qoe-title"
            role="heading"
            aria-level="3"
            aria-label={tViews('optimization.site')}
          >
            {tViews('optimization.site')} : {!globalFilter?.sites?.length ? <span className="site-name-spinner">&nbsp; <HbrSpinner alignment="no-label" size="small" /> </span>: siteName}
          </div>
        </div>
      </div>
    </nav>
  );
};

FilterView.propTypes = {
  globalFilter: PropTypes.object.isRequired,
};

// Export wrapped with Redux provider and AppHOC for app-level features
export default (AppHOC(memo(FilterView)));
